import { useCallback, useEffect, useMemo, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import type { ChatMessage, ChatSession } from '../types/chat';
import { loadChatHistory, saveChatHistory } from '../utils/localStorage';
import { sendMessage } from '../services/aiService';

const createMessage = (role: ChatMessage['role'], content: string): ChatMessage => ({
  id: uuidv4(),
  role,
  content,
  created_at: new Date().toISOString(),
});

const createChat = (): ChatSession => ({
  id: uuidv4(),
  title: 'New chat',
  messages: [],
});

const deriveTitle = (messages: ChatMessage[]): string => {
  const firstUser = messages.find((m) => m.role === 'user');
  if (!firstUser) return 'New chat';
  return firstUser.content.trim().slice(0, 40) || 'New chat';
};

export const useChat = () => {
  const [chats, setChats] = useState<ChatSession[]>([]);
  const [activeChatId, setActiveChatId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const stored = loadChatHistory();
    if (stored.length > 0) {
      setChats(stored);
      setActiveChatId(stored[0].id);
      return;
    }
    const first = createChat();
    setChats([first]);
    setActiveChatId(first.id);
  }, []);

  useEffect(() => {
    saveChatHistory(chats);
  }, [chats]);

  const activeChat = useMemo(
    () => chats.find((c) => c.id === activeChatId) || null,
    [chats, activeChatId]
  );

  const createNewChat = useCallback(() => {
    const next = createChat();
    setChats((prev) => [next, ...prev]);
    setActiveChatId(next.id);
    setError(null);
  }, []);

  const selectChat = useCallback((chatId: string) => {
    setActiveChatId(chatId);
    setError(null);
  }, []);

  const sendChat = useCallback(
    async (text: string) => {
      if (!activeChatId) return;
      const userMessage = createMessage('user', text);

      setChats((prev) =>
        prev.map((chat) =>
          chat.id === activeChatId
            ? { ...chat, messages: [...chat.messages, userMessage] }
            : chat
        )
      );

      setIsLoading(true);
      setError(null);

      try {
        const reply = await sendMessage(text);
        const aiMessage = createMessage('assistant', reply);

        setChats((prev) =>
          prev.map((chat) => {
            if (chat.id !== activeChatId) return chat;
            const updatedMessages = [...chat.messages, aiMessage];
            return {
              ...chat,
              messages: updatedMessages,
              title: deriveTitle(updatedMessages),
            };
          })
        );
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'AI service temporarily unavailable.';
        setError(message);
      } finally {
        setIsLoading(false);
      }
    },
    [activeChatId]
  );

  return {
    chats,
    activeChat,
    activeChatId,
    isLoading,
    error,
    createNewChat,
    selectChat,
    sendChat,
  };
};
